import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import { useState } from 'react';
import { ChevronLeft, ChevronRight, ChevronsUpDown, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from './Skeleton';
import { EmptyState } from './EmptyState';

const PAGE_SIZES = [10, 20, 50, 100] as const;

interface Props<T> {
  data: T[] | undefined;
  columns: ColumnDef<T, unknown>[];
  loading?: boolean;
  pageSize?: number;
  searchPlaceholder?: string;
  onRowClick?: (row: T) => void;
  emptyTitle?: string;
  emptyDescription?: string;
  rightSlot?: React.ReactNode;
}

export function DataTable<T extends object>({
  data,
  columns,
  loading,
  pageSize = 20,
  searchPlaceholder = 'Rechercher…',
  onRowClick,
  emptyTitle = 'Aucun résultat',
  emptyDescription,
  rightSlot,
}: Props<T>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  const table = useReactTable({
    data: data ?? [],
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize } },
  });

  const rows = table.getRowModel().rows;

  return (
    <div className="card overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-brand-border">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            placeholder={searchPlaceholder}
            className="input pl-8"
          />
        </div>
        {rightSlot}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-brand-border">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  return (
                    <th key={header.id} className="th">
                      <button
                        type="button"
                        disabled={!canSort}
                        onClick={header.column.getToggleSortingHandler()}
                        className={cn('flex items-center gap-1', canSort && 'cursor-pointer hover:text-brand-dark')}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {canSort && <ChevronsUpDown size={11} className={cn(sorted && 'text-brand-primary')} />}
                      </button>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-brand-border">
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i}>
                  {table.getAllColumns().map((c) => (
                    <td key={c.id} className="td">
                      <Skeleton className="h-4 w-24" />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={table.getAllColumns().length}>
                  <EmptyState title={emptyTitle} description={emptyDescription} />
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={row.id}
                  className={cn(onRowClick && 'table-row-hover')}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="td">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination footer with numbered pages + page-size selector */}
      {!loading && rows.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-brand-border text-xs text-gray-500">
          <div className="flex items-center gap-3">
            <span>
              {table.getFilteredRowModel().rows.length} entrée
              {table.getFilteredRowModel().rows.length > 1 ? 's' : ''}
            </span>
            <span className="flex items-center gap-1">
              Par page&nbsp;:
              <select
                value={table.getState().pagination.pageSize}
                onChange={(e) => table.setPageSize(Number(e.target.value))}
                className="bg-white border border-brand-border rounded px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-primary"
              >
                {PAGE_SIZES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </span>
          </div>
          <PageNumbers
            current={table.getState().pagination.pageIndex}
            total={table.getPageCount()}
            onChange={(i) => table.setPageIndex(i)}
            onPrev={() => table.previousPage()}
            onNext={() => table.nextPage()}
            canPrev={table.getCanPreviousPage()}
            canNext={table.getCanNextPage()}
          />
        </div>
      )}
    </div>
  );
}

interface PageNumbersProps {
  current: number;
  total: number;
  onChange: (i: number) => void;
  onPrev: () => void;
  onNext: () => void;
  canPrev: boolean;
  canNext: boolean;
}

/**
 * Numbered pagination with ellipsis: keeps the first/last and a window of 2
 * around the current page. Always renders 5–7 buttons regardless of the page
 * count so the footer doesn't reflow as you navigate.
 */
function PageNumbers({ current, total, onChange, onPrev, onNext, canPrev, canNext }: PageNumbersProps) {
  if (total <= 1) return null;

  const window = 1;
  const last = total - 1;
  const pages: (number | 'ellipsis-l' | 'ellipsis-r')[] = [];
  pages.push(0);
  if (current - window > 1) pages.push('ellipsis-l');
  for (let i = Math.max(1, current - window); i <= Math.min(last - 1, current + window); i++) {
    pages.push(i);
  }
  if (current + window < last - 1) pages.push('ellipsis-r');
  if (last > 0) pages.push(last);

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={onPrev}
        disabled={!canPrev}
        className="btn-ghost"
        aria-label="Page précédente"
      >
        <ChevronLeft size={14} />
      </button>
      {pages.map((p, idx) =>
        typeof p === 'number' ? (
          <button
            key={p}
            type="button"
            onClick={() => onChange(p)}
            className={cn(
              'min-w-[28px] h-7 px-2 rounded text-xs',
              p === current
                ? 'bg-brand-primary text-white font-medium'
                : 'hover:bg-gray-100 text-gray-600',
            )}
          >
            {p + 1}
          </button>
        ) : (
          <span key={`${p}-${idx}`} className="px-1 text-gray-400">
            …
          </span>
        ),
      )}
      <button
        type="button"
        onClick={onNext}
        disabled={!canNext}
        className="btn-ghost"
        aria-label="Page suivante"
      >
        <ChevronRight size={14} />
      </button>
    </div>
  );
}
